import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { EnvironmentDataProvider } from '@lib.core/environment/interface';
import { ClientLocationIdService } from '@lib.shared/service-registry/interface/client-location-id-service';
import { ProfileResource } from '@lib.resources/profile/resource';
import { AspectResourceV2 } from '@lib.resources/indicator/resources/indicator-resource-v2';
import { AspectResource } from '@lib.resources/indicator/resources/indicator-resource';
import { StoryResource } from '@lib.resources/story/resource';
import { ReportsResource } from '@lib.resources/report/resource';
import { PublicationResource } from '@lib.resources/publication/resource';

@Injectable({
	providedIn: 'root'
})
export class ServiceRegistry {

	private readonly profileService$$ = new BehaviorSubject<ProfileResource | undefined>(undefined);
	private readonly aspectService$$ = new BehaviorSubject<AspectResource | undefined>(undefined);
	private readonly storyResource$$ = new BehaviorSubject<StoryResource | undefined>(undefined);
	private readonly aspectServiceV2$$ = new BehaviorSubject<AspectResourceV2 | undefined>(undefined);
	private readonly clientLocationIdService$$ = new BehaviorSubject<ClientLocationIdService | undefined>(undefined);
	private readonly publicationResource$$ = new BehaviorSubject<PublicationResource | undefined>(undefined);
	private readonly reportsResource$$ = new BehaviorSubject<ReportsResource | undefined>(undefined);
	private readonly environmentService$$ = new BehaviorSubject<EnvironmentDataProvider | undefined>(undefined);
	private readonly profileService$ = this.profileService$$.asObservable();
	private readonly aspectService$ = this.aspectService$$.asObservable();
	private readonly storyResource$ = this.storyResource$$.asObservable();
	private readonly aspectServiceV2$ = this.aspectServiceV2$$.asObservable();
	private readonly clientLocationIdService$ = this.clientLocationIdService$$.asObservable();
	private readonly publicationResource$ = this.publicationResource$$.asObservable();
	private readonly reportsResource$ = this.reportsResource$$.asObservable();
	private readonly environmentService$ = this.environmentService$$.asObservable();

	setProfileService(service: ProfileResource) {
		this.profileService$$.next(service);
	}

	getProfileService(): Observable<ProfileResource> {
		return this.profileService$.pipe(filter((service: ProfileResource | undefined) => service !== undefined));
	}

	setAspectService(service: AspectResource) {
		this.aspectService$$.next(service);
	}

	getAspectService(): Observable<AspectResource> {
		return this.aspectService$.pipe(filter((service: AspectResource | undefined) => service !== undefined));
	}

	setStoryResource(resource: StoryResource) {
		this.storyResource$$.next(resource);
	}

	getStoryResource(): Observable<StoryResource> {
		return this.storyResource$.pipe(filter((service: StoryResource | undefined) => service !== undefined));
	}

	setAspectServiceV2(resource: AspectResourceV2) {
		this.aspectServiceV2$$.next(resource);
	}

	getAspectServiceV2(): Observable<AspectResourceV2> {
		return this.aspectServiceV2$.pipe(filter((service: AspectResourceV2 | undefined) => service !== undefined));
	}

	setClientLocationIdService(service: ClientLocationIdService) {
		this.clientLocationIdService$$.next(service);
	}

	getClientLocationIdService(): Observable<ClientLocationIdService> {
		return this.clientLocationIdService$.pipe(filter((service: ClientLocationIdService | undefined) => service !== undefined));
	}

	setPublicationResource(resource: PublicationResource) {
		this.publicationResource$$.next(resource);
	}

	getPublicationResource(): Observable<PublicationResource> {
		return this.publicationResource$.pipe(filter((service: PublicationResource | undefined) => service !== undefined));
	}
	
	setReportsResource(resource: ReportsResource) {
		this.reportsResource$$.next(resource);
	}

	getReportsResource(): Observable<ReportsResource> {
		return this.reportsResource$.pipe(filter((service: ReportsResource | undefined) => service !== undefined));
	}

	setEnvironmentService(service: EnvironmentDataProvider) {
		this.environmentService$$.next(service);
	}

	getEnvironmentService() {
		return this.environmentService$.pipe(filter((service: EnvironmentDataProvider | undefined) => service !== undefined));
	}
}
